import { useState } from "react";
import { Copy, Check, Phone, MapPin, ExternalLink, Mail, MessageCircle } from "lucide-react";
import { mediaUrl } from "@/lib/api";

// Renders an array of content blocks inside a section detail view.
export function BlockRenderer({ blocks = [], T, brand }) {
  const [copied, setCopied] = useState("");
  const copy = (v, k) => { navigator.clipboard?.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };
  const surface = { background: T.surface, borderColor: T.border };
  const label = { color: T.muted };
  const text = { color: T.text };

  const ctaHref = (a, v) => ({
    map: `https://maps.google.com/?q=${encodeURIComponent(v)}`,
    link: v, call: `tel:${v}`, email: `mailto:${v}`,
    whatsapp: `https://wa.me/${(v || "").replace(/[^0-9]/g, "")}`,
  }[a] || v);

  return (
    <div className="space-y-4">
      {blocks.map((b) => {
        const c = b.content || {};
        switch (b.type) {
          case "heading":
            return <h3 key={b.id} className="font-serif-display text-xl" style={text}>{c.text}</h3>;
          case "text":
            return c.text ? <p key={b.id} className="text-[15px] leading-relaxed whitespace-pre-wrap" style={{ color: T.dark ? T.muted : "#4B5563" }}>{c.text}</p> : null;
          case "image":
            return c.url ? (
              <figure key={b.id}>
                <img src={mediaUrl(c.url)} alt={c.caption || ""} className="w-full rounded-2xl object-cover max-h-64" />
                {c.caption && <figcaption className="text-xs mt-1.5" style={label}>{c.caption}</figcaption>}
              </figure>
            ) : null;
          case "info":
            return (
              <div key={b.id} className="rounded-xl border p-3.5" style={surface}>
                <p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{c.label}</p>
                <p className="text-[15px] mt-0.5 whitespace-pre-wrap" style={text}>{c.value}</p>
              </div>
            );
          case "hours":
            return (
              <div key={b.id} className="inline-flex items-center gap-2 rounded-full border px-4 py-2" style={surface}>
                <span className="text-xs" style={label}>{c.label}</span>
                <span className="font-semibold" style={text}>{c.value}</span>
              </div>
            );
          case "wifi":
            return (
              <div key={b.id} className="space-y-2">
                <Row surface={surface} label={label} text={text} k="Rete" v={c.network} onCopy={() => copy(c.network, b.id + "n")} did={copied === b.id + "n"} />
                <Row surface={surface} label={label} text={text} k="Password" v={c.password} mono onCopy={() => copy(c.password, b.id + "p")} did={copied === b.id + "p"} />
              </div>
            );
          case "phone":
            return (
              <a key={b.id} href={`tel:${c.value}`} className="rounded-xl border p-3.5 flex items-center justify-between" style={surface}>
                <div><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{c.label}</p><p className="text-[15px] mt-0.5" style={text}>{c.value}</p></div>
                <Phone className="w-4 h-4 text-emerald-500" />
              </a>
            );
          case "address":
            return (
              <a key={b.id} href={ctaHref("map", c.value)} target="_blank" rel="noreferrer" className="rounded-xl border p-3.5 flex items-center justify-between" style={surface}>
                <div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>Indirizzo</p><p className="text-[15px] mt-0.5 truncate" style={text}>{c.value}</p></div>
                <span className="text-xs font-semibold flex items-center gap-1 shrink-0" style={{ color: brand }}>Mappa <MapPin className="w-3.5 h-3.5" /></span>
              </a>
            );
          case "list":
            return (
              <ul key={b.id} className="space-y-2">
                {(c.items || []).filter(Boolean).map((it, i) => (
                  <li key={i} className="flex gap-2.5 text-[15px]" style={{ color: T.dark ? T.muted : "#4B5563" }}>
                    <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: brand }} />{it}
                  </li>
                ))}
              </ul>
            );
          case "steps": {
            const steps = (c.steps || []).filter((s) => (s.text || s.image));
            if (!steps.length && !c.title) return null;
            return (
              <div key={b.id} className="space-y-3">
                {c.title && <h3 className="font-serif-display text-lg" style={text}>{c.title}</h3>}
                <ol className="space-y-3">
                  {steps.map((s, i) => (
                    <li key={i} className="rounded-2xl border overflow-hidden" style={surface}>
                      {s.image && <img src={mediaUrl(s.image)} alt="" className="w-full max-h-56 object-cover" />}
                      <div className="p-3.5 flex gap-3">
                        <span className="w-6 h-6 rounded-full grid place-items-center text-xs font-bold text-white shrink-0" style={{ background: brand }}>{i + 1}</span>
                        <p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={{ color: T.dark ? T.muted : "#4B5563" }}>{s.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
          case "button": {
            if (b.content.action === "copy") {
              return <button key={b.id} onClick={() => copy(c.value, b.id)} className="w-full rounded-xl py-3 font-semibold text-white flex items-center justify-center gap-2" style={{ background: brand }}>{copied === b.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{c.label}</button>;
            }
            const Ico = { call: Phone, whatsapp: MessageCircle, email: Mail, map: MapPin }[c.action] || ExternalLink;
            return <a key={b.id} href={ctaHref(c.action, c.value)} target="_blank" rel="noreferrer" className="w-full rounded-xl py-3 font-semibold text-white flex items-center justify-center gap-2" style={{ background: brand }}><Ico className="w-4 h-4" />{c.label}</a>;
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

const Row = ({ surface, label, text, k, v, mono, onCopy, did }) => (
  <div className="rounded-xl border p-3.5 flex items-center justify-between gap-3" style={surface}>
    <div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{k}</p><p className={`text-[15px] mt-0.5 truncate ${mono ? "font-mono" : ""}`} style={text}>{v || "—"}</p></div>
    {v && <button onClick={onCopy} className="shrink-0 p-1.5" style={label}>{did ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}</button>}
  </div>
);
